import { faImage } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { PropsWithChildren, useCallback, useState } from 'react'
import { Content, Indicators, Modal, Button } from 'ui'
import { useUploadAvatarMutation } from '../../../queries'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useProfileById } from '../../../queries/useProfileById'
import { normalizeProfile } from '../../../utils/profileHelpers'
import { concatStyles } from 'utils'
import { useDropzone } from 'react-dropzone'

const AVATAR_SIZE = 278

export interface DropContainerProps {
  active: boolean
  backgroundUrl?: string | null
}

const DropContainer: React.FC<PropsWithChildren<DropContainerProps>> = ({
  active,
  backgroundUrl,
  children,
}) => {
  const containerClasses = concatStyles(
    'flex justify-center items-center my-4',
    {
      'border-10': active,
      'border-0': !active,
      'border-transparent': !active,
      'border-actionable': active,
    }
  )

  const backgroundStyle = {
    backgroundImage: backgroundUrl ? `url("${backgroundUrl}")` : undefined,
    backgroundColor: active
      ? 'rgba(255, 0, 0, 0.8)' // replace with transparentize(0.8)(p.theme.colorActionable) value
      : '#ddd', // replace with p.theme.colorContentAreaBorder value
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    height: AVATAR_SIZE,
    width: AVATAR_SIZE,
  }

  return (
    <div className={containerClasses} style={backgroundStyle}>
      {children}
    </div>
  )
}

interface IUpdateAvatarModalProps {
  onDismiss: () => void
  onSuccess: (fileKey: string, url: string) => Promise<void> | void
  title: string
  description: string
  loading: boolean
  uniqueId?: string
  profileId?: string // Added profileId parameter
}

const dataURItoBlob = (dataURI: string) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(',')[1])

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length)

  // create a view into the buffer
  const ia = new Uint8Array(ab)

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }

  // write the ArrayBuffer to a blob, and you're done
  const blob = new Blob([ab], { type: mimeString })
  return blob
}

interface IResizedImage {
  dataUrl: string
  resizedFile: Blob
}

/**
 * Resizes and crops a file upload.
 * @param preview An image preview from the file upload hook.
 */
const resizeFile = (preview: any, sizeLimit?: number): Promise<IResizedImage> =>
  new Promise((resolve) => {
    const image = new Image()
    image.onload = () => {
      const maxDimension = Math.min(image.width, image.height)
      const maxSize = Math.min(maxDimension, sizeLimit || 300)
      const sx = image.width - maxDimension
      const sy = image.height - maxDimension

      const canvas = document.createElement('canvas')
      canvas.width = maxSize
      canvas.height = maxSize

      const context = canvas.getContext('2d')
      if (context) {
        context.drawImage(
          image,
          sx,
          sy,
          maxDimension,
          maxDimension,
          0,
          0,
          maxSize,
          maxSize
        )
        const dataUrl = canvas.toDataURL(preview.file?.type)
        const resizedFile = dataURItoBlob(dataUrl)
        resolve({ dataUrl, resizedFile })
      }
    }
    image.src = URL.createObjectURL(preview?.file as any)
  })

export const UpdateAvatarModal: React.FC<IUpdateAvatarModalProps> = ({
  onSuccess: handleUploadSuccess,
  description,
  onDismiss: handleDismiss,
  title,
  loading: externalLoading,
  profileId,
}) => {
  // Choose the appropriate data source based on profileId
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
  } = useCurrentUser()
  const {
    data: specificProfile,
    isLoading: isSpecificProfileLoading,
  } = useProfileById(profileId)

  // Use profileId if provided, otherwise use current user
  const profile = profileId ? specificProfile : currentUser
  const loadingProfile = profileId
    ? isSpecificProfileLoading
    : isCurrentUserLoading

  // Normalize profile data for consistent property access
  const normalizedProfile = normalizeProfile(profile)

  const { uploadAvatar, loading: uploadLoading } = useUploadAvatarMutation()

  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [resizedImage, setResizedImage] = useState<Blob | null>(null)
  const [uploading, setUploading] = useState(false)

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      try {
        // Resize the image
        const filePreview = { file }
        const preview = await resizeFile(filePreview as any)
        setLocalPreview(preview.dataUrl)
        setResizedImage(preview.resizedFile)
      } catch (error) {
        console.error('Error processing file:', error)
      }
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
    noClick: false,
    noKeyboard: false,
    noDrag: false,
  })

  const handleFileKey = async (fileKey: string, url: string) => {
    await handleUploadSuccess(fileKey, url)
    if (localPreview) {
      URL.revokeObjectURL(localPreview)
    }
    handleDismiss()
  }

  const uploadFile = async () => {
    if (resizedImage && normalizedProfile) {
      setUploading(true)
      try {
        // Create a unique filename that includes user identifier
        const userId = normalizedProfile.userId || normalizedProfile.id
        const uniqueId = `avatar-${userId}-${Date.now()}`
        const resizedFile = new File([resizedImage], uniqueId, {
          type: 'image/jpeg',
        })

        // Upload the resized image with profile context
        const result = await uploadAvatar(resizedFile, profileId)

        // Handle success
        await handleFileKey(result.fileKey, result.url)
      } catch (error) {
        console.error('Error uploading avatar:', error)
        setUploading(false)
      }
    }
  }

  const backgroundUrl = localPreview || ''
  const hasPreview = !!localPreview
  const readyForSubmission = hasPreview && !uploading
  const loading =
    loadingProfile || uploadLoading || externalLoading || uploading
  return (
    <Modal.View
      onDismiss={handleDismiss}
      loading={loading}
      disabled={uploading}
    >
      <div className="flex p-1 pt-0 flex-col items-center w-full">
        <Content.HeadingSmall className="text-center w-full mb-4">
          {title}
        </Content.HeadingSmall>
        <div
          {...(getRootProps() as any)}
          style={{
            backgroundImage: backgroundUrl && `url(${backgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: 300,
            width: 300,
          }}
          className="rounded-lg flex items-center justify-center"
        >
          <input {...(getInputProps() as any)} />
          {backgroundUrl && backgroundUrl.length > 0 ? null : (
            <div className="p-2 mx-auto">
              <section className="text-center">
                <FontAwesomeIcon size="4x" icon={faImage} />
                <Content.Body className="mt-2">{description}</Content.Body>
              </section>
            </div>
          )}
        </div>
        {uploading ? (
          <div className="w-full flex justify-center mt-4">
            <Indicators.Spinner size="3x" />
          </div>
        ) : (
          <div className="flex flex-row justify-center space-x-4 mt-6 w-full">
            <Button
              variant="outline"
              onClick={handleDismiss}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={uploadFile}
              disabled={!readyForSubmission}
            >
              Confirm
            </Button>
          </div>
        )}
      </div>
    </Modal.View>
  )
}
